import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { ApiError } from '../lib/api.js';
import { STATUS_LABELS, useCreateEditRequest, useProjects } from '../lib/portal.js';

/* ============================================================================
   /edit-requests/new — ask for a change.
   ----------------------------------------------------------------------------
   No file upload, deliberately. `EditRequest.attachments` exists in the schema
   as object-storage keys, and there is no object storage in this product yet —
   a picker that cannot upload anything is worse than none. Photos go over
   WhatsApp, which is where this audience already sends them, and the text here
   is what makes the request findable later.
   ========================================================================== */

export function NewEditRequestPage() {
  const navigate = useNavigate();
  const projects = useProjects();
  const create = useCreateEditRequest();

  const [projectId, setProjectId] = useState('');
  const [message, setMessage] = useState('');

  const items = projects.data?.projects ?? [];
  /* One project is the normal case, so it is selected rather than presented as
     a choice of one — which reads as a question with an obvious answer. */
  const chosen = projectId || (items.length === 1 ? (items[0]?.id ?? '') : '');

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    create.mutate(
      { projectId: chosen, message },
      { onSuccess: () => void navigate('/edit-requests', { replace: true }) },
    );
  }

  const fieldError = (name: string): string | undefined =>
    create.error instanceof ApiError ? create.error.fields[name] : undefined;

  const formError =
    create.error instanceof ApiError && Object.keys(create.error.fields).length === 0
      ? create.error.message
      : null;

  if (projects.isPending) return <p className="muted">Load ho raha hai…</p>;

  if (items.length === 0) {
    return (
      <>
        <p className="alert" role="alert">
          Abhi koi project nahi hai jisme change ho sake.
        </p>
        <Link to="/" className="btn btn--ghost">
          Wapas
        </Link>
      </>
    );
  }

  return (
    <>
      <h1 className="h1">Kya badalna hai?</h1>

      <form className="card stack" onSubmit={onSubmit} noValidate>
        {items.length > 1 ? (
          <div className="field">
            <label htmlFor="projectId">Kaunsi site</label>
            <select
              id="projectId"
              value={chosen}
              onChange={(event) => setProjectId(event.target.value)}
            >
              <option value="">Chuniye…</option>
              {items.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.domain ?? project.plan} · {STATUS_LABELS[project.status]}
                </option>
              ))}
            </select>
            {fieldError('projectId') ? (
              <span className="field__error" role="alert">
                {fieldError('projectId')}
              </span>
            ) : null}
          </div>
        ) : null}

        <div className="field">
          <label htmlFor="message">Change</label>
          <textarea
            id="message"
            rows={6}
            maxLength={2000}
            placeholder="Jaise: home page par naya phone number lagana hai, aur Sunday ki timing hata dijiye."
            value={message}
            onChange={(event) => setMessage(event.target.value)}
          />
          <span className="muted small">
            Jitna saaf likhenge, utni jaldi ho jayega. Photo WhatsApp par bhej dijiye.
          </span>
          {fieldError('message') ? (
            <span className="field__error" role="alert">
              {fieldError('message')}
            </span>
          ) : null}
        </div>

        {formError ? (
          <p className="alert" role="alert">
            {formError}
          </p>
        ) : null}

        <div className="row">
          <button
            className="btn"
            type="submit"
            disabled={create.isPending || message.trim() === '' || chosen === ''}
          >
            {create.isPending ? 'Bhej rahe hain…' : 'Bhej dijiye'}
          </button>
          <Link to="/edit-requests" className="btn btn--ghost">
            Cancel
          </Link>
        </div>
      </form>
    </>
  );
}
